import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { TransitionGroup } from 'react-transition-group';

import Overlay from '../Widget/Overlay';
import OverlayPartner from '../Widget/OverlayPartner';
import OverlayPublisher from '../Widget/OverlayPublisher';
import WidgetHeader from '../Widget/WidgetHeader';

import { AutoScalingButtonSet } from '../../components/Buttons/AutoScalingButtonSet';
import { isLongText } from '../../components/Shared/commonFunctions';
import AddressPopup from '../../components/VoteRegistration/AddressPopup';
import AnswerButton from '../../components/Assessment/AnswerButton';
import CssTransition from '../../components/Transition/CssTransition';
import ShareDrawer from '../../components/ShareDrawer';
import ToasterAlert from '../../components/Donation/ToasterAlert';

import { Body, Container, RockTheVoteLabel } from '../DefaultStyles/Details';
import {
    Content,
    Description,
    Details,
    DetailsContainer,
    Footer,
    RTVLogo,
    Title
} from '../DefaultStyles/Shared';

import {
    onBackWithinCard,
    setOverlayPartnerVisible,
    setExitingCardProps,
    getExitingCardProps
} from '../Widget/actions';
import { onShowAddressPopup } from './actions';

import { CardTypes } from '../../utils/enums';

import { RockTheVoteDetails } from '../../components/Shared/rockTheVoteDetails';

class VoteRegistrationDetails extends React.Component {
    componentDidMount() {
        this.forceUpdate();
    }

    render() {
        return (
            <Container>
                <Overlay />
                <OverlayPublisher />
                <OverlayPartner
                    transitionType="right-to-left"
                    logo={RTVLogo}
                    name={this.props.rockTheVoteTitle}
                    description={this.props.rockTheVoteDescription}
                />
                <TransitionGroup>
                    {this.props.addressValidationError && (
                        <CssTransition key="alert" in={this.props.addressValidationError}>
                            <ToasterAlert content={this.props.addressValidationError} />
                        </CssTransition>
                    )}
                </TransitionGroup>
                <Body>
                    <WidgetHeader />
                    <Content isContactAction={false} isSingleAction={true}>
                        <Title
                            isLongText={isLongText(this.props.title, 60)}
                            isExtraLongText={isLongText(this.props.title, 95)}
                            onClick={e =>
                                this.props.setOverlayPartnerVisible(true, e, false)
                            }
                            dangerouslySetInnerHTML={{
                                __html: this.props.title
                            }}
                        />
                        <DetailsContainer>
                            <Details>
                                <Description
                                    onClick={e =>
                                        this.props.setOverlayPartnerVisible(
                                            true,
                                            e,
                                            false
                                        )
                                    }
                                    isLongText={isLongText(this.props.subTitle, 90)}
                                    isExtraLongText={isLongText(this.props.subTitle, 160)}
                                    dangerouslySetInnerHTML={{
                                        __html: this.props.subTitle
                                    }}
                                />
                                <RockTheVoteLabel
                                    color={this.props.theme.colors.primary}
                                    onClick={e =>
                                        this.props.setOverlayPartnerVisible(true, e, true)
                                    }
                                >
                                    {this.props.rockTheVoteLabel}
                                </RockTheVoteLabel>
                            </Details>
                        </DetailsContainer>
                    </Content>
                    <AutoScalingButtonSet mobileStyled={true} mobileLeftPosition={8}>
                        <AnswerButton
                            text={this.props.registerButtonText}
                            useLoadingSpinner={false}
                            onClick={() => {
                                this.props.setExitingCardProps(
                                    'voteRegistration',
                                    this.props,
                                    this.props.activeAction
                                );
                                this.props.onShowAddressPopup(
                                    true,
                                    this.props.actionIdentifier,
                                    this.props.actionInitiated
                                );
                            }}
                        />
                    </AutoScalingButtonSet>
                    <Footer>
                        <ShareDrawer
                            beforeBack={() => {
                                this.props.setExitingCardProps(
                                    'voteRegistration',
                                    this.props,
                                    this.props.activeAction
                                );
                            }}
                            goBack={() => {
                                return this.props.card.type === CardTypes.actionChoice
                                    ? this.props.onBackWithinCard(
                                          this.props.actionIdentifier,
                                          this.props.card,
                                          true
                                      )
                                    : null;
                            }}
                            fullwidth={this.props.card.type === CardTypes.actionChoice}
                            rightAlign={this.props.card.type !== CardTypes.actionChoice}
                            hasBackButton={
                                this.props.card.type === CardTypes.actionChoice
                            }
                            backgroundColor={'#fff'}
                        />
                    </Footer>
                    <TransitionGroup>
                        {this.props.showAddressPopup && (
                            <CssTransition key="alert" in={this.props.showAddressPopup}>
                                <AddressPopup
                                    onBack={() => {
                                        this.props.onShowAddressPopup(
                                            false,
                                            this.props.actionIdentifier
                                        );
                                    }}
                                />
                            </CssTransition>
                        )}
                    </TransitionGroup>
                </Body>
            </Container>
        );
    }
}

VoteRegistrationDetails.propTypes = {
    addressValidationError: PropTypes.any,
    actionIdentifier: PropTypes.string.isRequired,
    actionInitiated: PropTypes.bool.isRequired,
    activeAction: PropTypes.object.isRequired,
    card: PropTypes.object.isRequired,
    onBackWithinCard: PropTypes.func.isRequired,
    onShowAddressPopup: PropTypes.func,
    registerButtonText: PropTypes.string.isRequired,
    rockTheVoteDescription: PropTypes.string,
    rockTheVoteLabel: PropTypes.string.isRequired,
    rockTheVoteTitle: PropTypes.string,
    setExitingCardProps: PropTypes.func.isRequired,
    setOverlayPartnerVisible: PropTypes.func.isRequired,
    showAddressPopup: PropTypes.bool,
    subTitle: PropTypes.string,
    title: PropTypes.string,
    theme: PropTypes.object
};

export default connect(
    (state, props) => {
        const exitingProps = getExitingCardProps(
            'voteRegistration',
            ['/voteRegistration', '/voteRegistration/start'],
            props.location.pathname,
            state
        );
        if (exitingProps) return exitingProps;

        const defaultTitle = 'Register to vote.';
        const defaultSubtitle =
            "Remember, you can't vote in the upcoming election unless you're registered. " +
            'Take a few minutes and register to vote today.';

        return {
            activeAction: state.widget.activeAction,
            addressValidationError: state.widget.addressValidationError,
            actionIdentifier: state.widget.activeAction.identifier,
            actionInitiated: state.voteRegistration.actionInitiated,
            card: state.widget.cards[state.widget.activeAction.order - 1],
            registerButtonText: state.widget.activeAction.customButtonText || 'REGISTER',
            rockTheVoteDescription: RockTheVoteDetails.description,
            rockTheVoteLabel: RockTheVoteDetails.label,
            rockTheVoteTitle: RockTheVoteDetails.title,
            showAddressPopup: state.voteRegistration.showAddressPopup,
            subTitle: state.widget.activeAction.text || defaultSubtitle,
            title: state.widget.activeAction.title || defaultTitle,
            theme: state.widget.theme
        };
    },
    {
        onBackWithinCard,
        onShowAddressPopup,
        setExitingCardProps,
        setOverlayPartnerVisible
    }
)(VoteRegistrationDetails);
